var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Controller, useFormContext, useForm } from 'react-hook-form';
import { DEFAULT_DURATION_PRESETS, DEFAULT_TIME_PRESETS, isValidDuration, MIN_DURATION_TIME_MESSAGE, timeDurationFormSchema, } from 'root/src/shared/giveaways/giveaways.constants';
import * as Popover from '@radix-ui/react-popover';
import { forwardRef, memo } from 'react';
import { ButtonNew } from 'root/src/client/v2/common/components/form/ButtonNew';
import { SelectSlider } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/components/SelectSlider';
import { zodResolver } from '@hookform/resolvers/zod';
import { twMerge } from 'tailwind-merge';
export const TimeDurationField = memo(() => {
    const form = useFormContext();
    return (<div className="flex flex-initial flex-col justify-center items-center w-[90%]">
			<p className="uppercase font-medium text-light-5 my-2">Set giveaway duration</p>
			<Controller name="durationTime" control={form.control} render={({ field }) => {
            return (<ul className="flex flex-col justify-between w-full sm:flex-row gap-2 my-2">
							{DEFAULT_DURATION_PRESETS.map((preset) => {
                    return (<li key={preset.label}>
										<TimePresetButton data-selected={field.value === preset.value} onClick={() => {
                            field.onChange(preset.value);
                        }} color={TimePresetButtonColor.Primary}>
											{preset.label}
										</TimePresetButton>
									</li>);
                })}
						</ul>);
        }}/>
			<CustomDateField />
		</div>);
});
const CustomDateField = memo(() => {
    const form = useFormContext();
    return (<Controller name="durationTime" control={form.control} render={({ field, fieldState }) => {
            const isPresetValue = DEFAULT_DURATION_PRESETS.some(preset => preset.value === field.value);
            return (<Popover.Root>
						<Popover.Trigger asChild>
							<TimePresetButton data-valid={Boolean(isPresetValue || !fieldState.error)} data-selected={!isPresetValue}>
								+ Set custom
							</TimePresetButton>
						</Popover.Trigger>
						<Popover.Portal>
							<Popover.Content className="z-[10000] border-4 border-dark-1 motion-safe:animate-slide-down flex flex-initial flex-col justify-center items-center gap-5 bg-dark-3 px-8 py-5 rounded-lg shadow-2xl drop-shadow-2xl" collisionPadding={10} sideOffset={5}>
								<div className="flex flex-col gap-2">
									<p className="text-light-4 uppercase text-center">Set a custom
										duration:
									</p>
									<p className="uppercase text-sm text-center">The duration of your giveaways</p>
								</div>
								<div className="w-1/4 h-[1px] bg-light-5"/>
								<div className="w-full flex gap-4 justify-center items-center">
									<TimeDurationForm onSubmit={(formData) => {
                    const { days, hours, minutes, } = formData;
                    const duration = (days || 0) * 1000 * 60 * 60 * 24
                        + (hours || 0) * 1000 * 60 * 60
                        + (minutes || 0) * 1000 * 60;
                    field.onChange(duration);
                }} value={field.value}/>
								</div>
							</Popover.Content>
						</Popover.Portal>
					</Popover.Root>);
        }}/>);
});
const useTimeDurationForm = (props) => {
    const form = useForm({
        resolver: zodResolver(timeDurationFormSchema),
        mode: 'all',
        reValidateMode: 'onChange',
        defaultValues: {
            days: props.value ? Math.floor(props.value / (1000 * 60 * 60 * 24)) : 0,
            hours: props.value ? Math.floor(props.value / (1000 * 60 * 60)) % 24 : 0,
            minutes: props.value ? Math.floor(props.value / (1000 * 60)) % 60 : 0,
        },
    });
    const hours = form.watch('hours');
    const minutes = form.watch('minutes');
    const days = form.watch('days');
    const isValid = isValidDuration({ days, hours, minutes });
    return {
        form,
        isValid,
    };
};
export const TimeDurationForm = memo((props) => {
    const { form, isValid } = useTimeDurationForm(props);
    return (<form onSubmit={form.handleSubmit(props.onSubmit)} className="flex flex-col gap-6">
			<div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
				{DEFAULT_TIME_PRESETS.map((preset) => {
            return (<Controller key={preset.label} name={preset.label} control={form.control} render={({ field }) => {
                    return (<TimeDurationInput label={preset.label} options={preset.options} value={field.value} onChange={field.onChange}/>);
                }}/>);
        })}
			</div>
			<Popover.Close asChild>
				<ButtonNew onClick={() => {
            void form.handleSubmit(props.onSubmit)();
        }} className="font-bold" type="submit" disabled={!isValid}>
					Set duration
				</ButtonNew>
			</Popover.Close>
			{!isValid && <span className="text-pink text-sm text-center">{MIN_DURATION_TIME_MESSAGE}</span>}
		</form>);
});
const TimeDurationInput = memo((props) => {
    const { label, options, value, onChange } = props;
    const selectedOption = options.find(option => option.value === value);
    return (<div key={label} className="flex flex-col justify-center items-center gap-4">
			<label className="uppercase text-light-3 text-sm" htmlFor={label}>
				{label}
			</label>
			<SelectSlider label={(selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label) || '-'} options={options} onChange={onChange} value={value}/>
		</div>);
});
var TimePresetButtonColor;
(function (TimePresetButtonColor) {
    TimePresetButtonColor["Primary"] = "primary";
    TimePresetButtonColor["Teal"] = "teal";
})(TimePresetButtonColor || (TimePresetButtonColor = {}));
const timePresetButtonColorClasses = {
    [TimePresetButtonColor.Primary]: 'border-secondary hover:border-primary hover:bg-secondary data-[state=open]:bg-primary data-[selected=true]:bg-primary',
    [TimePresetButtonColor.Teal]: 'border-teal hover:border-teal hover:bg-teal data-[state=open]:bg-teal data-[selected=true]:bg-teal data-[selected=true]:text-dark-1 data-[state=open]:text-dark-1 hover:text-dark-1 data-[valid=false]:text-white data-[valid=false]:bg-pink',
};
const TimePresetButton = memo(forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    const color = props.color || TimePresetButtonColor.Primary;
    const colorClasses = timePresetButtonColorClasses[color];
    return (<button ref={ref} type="button" className={twMerge('w-full sm:py-2 px-6 py-4 motion-safe:data-[state=open]:animate-scale-up motion-safe:data-[selected=true]:animate-scale-up motion-safe:duration-150 data-[state=open]:bg-teal data-[selected=true]:bg-teal capitalize font-bold flex items-center justify-center border border-teal rounded-lg text-sm', colorClasses, className)} {...props}/>);
}));
